import {TagProps} from 'antd';

import {LangKeyType} from '../../provider/locale/translation/type';

import {
    AiMonitoringAnswersParametersType,
    AiMonitoringRunStatusEnum,
    AiMonitoringScoresParametersType,
    AiMonitoringVisibilitySignalEnum,
} from './ai-brand-monitoring-type';

export const aiMonitoringQueryKey = {
    config: (brandId: number | null): Array<unknown> => ['ai-monitoring-config', brandId],
    availableCompetitors: (brandId: number | null): Array<unknown> => ['ai-monitoring-available-competitors', brandId],
    run: (brandId: number | null, runId: string | null): Array<unknown> => ['ai-monitoring-run', brandId, runId],
    analysis: (brandId: number | null, runId: string | null): Array<unknown> => [
        'ai-monitoring-analysis',
        brandId,
        runId,
    ],
    scores: (brandId: number | null, parameters: AiMonitoringScoresParametersType): Array<unknown> => [
        'ai-monitoring-scores',
        brandId,
        parameters,
    ],
    answers: (brandId: number | null, parameters: AiMonitoringAnswersParametersType): Array<unknown> => [
        'ai-monitoring-answers',
        brandId,
        parameters,
    ],
    recommendations: (brandId: number | null, runId: string | null): Array<unknown> => [
        'ai-monitoring-recommendations',
        brandId,
        runId,
    ],
} as const;

// antd Tag colors keyed by run status.
export const aiMonitoringRunStatusColorMap: Record<AiMonitoringRunStatusEnum, TagProps['color']> = {
    [AiMonitoringRunStatusEnum.queued]: 'default',
    [AiMonitoringRunStatusEnum.running]: 'processing',
    [AiMonitoringRunStatusEnum.success]: 'success',
    [AiMonitoringRunStatusEnum.partial]: 'warning',
    [AiMonitoringRunStatusEnum.failed]: 'error',
};

// Localized label keys for run status tags.
export const aiMonitoringRunStatusLangKeyMap: Record<AiMonitoringRunStatusEnum, LangKeyType> = {
    [AiMonitoringRunStatusEnum.queued]: 'AI_BRAND_MONITORING__RUN_STATUS__QUEUED',
    [AiMonitoringRunStatusEnum.running]: 'AI_BRAND_MONITORING__RUN_STATUS__RUNNING',
    [AiMonitoringRunStatusEnum.success]: 'AI_BRAND_MONITORING__RUN_STATUS__SUCCESS',
    [AiMonitoringRunStatusEnum.partial]: 'AI_BRAND_MONITORING__RUN_STATUS__PARTIAL',
    [AiMonitoringRunStatusEnum.failed]: 'AI_BRAND_MONITORING__RUN_STATUS__FAILED',
};

// Localized labels for the web-presence signals, shared by the settings checkboxes and the
// visibility card on the dashboard.
export const aiMonitoringSignalLangKeyMap: Record<AiMonitoringVisibilitySignalEnum, LangKeyType> = {
    [AiMonitoringVisibilitySignalEnum.officialSite]: 'AI_BRAND_MONITORING__SIGNAL__OFFICIAL_SITE',
    [AiMonitoringVisibilitySignalEnum.socials]: 'AI_BRAND_MONITORING__SIGNAL__SOCIALS',
    [AiMonitoringVisibilitySignalEnum.directories]: 'AI_BRAND_MONITORING__SIGNAL__DIRECTORIES',
    [AiMonitoringVisibilitySignalEnum.reviews]: 'AI_BRAND_MONITORING__SIGNAL__REVIEWS',
    [AiMonitoringVisibilitySignalEnum.media]: 'AI_BRAND_MONITORING__SIGNAL__MEDIA',
    [AiMonitoringVisibilitySignalEnum.communities]: 'AI_BRAND_MONITORING__SIGNAL__COMMUNITIES',
};

export const AI_MONITORING_ANSWERS_PAGE_SIZE = 10;

// Run statuses that are final (no more polling / data won't change).
const AI_MONITORING_TERMINAL_RUN_STATUSES: ReadonlySet<AiMonitoringRunStatusEnum> = new Set([
    AiMonitoringRunStatusEnum.success,
    AiMonitoringRunStatusEnum.partial,
    AiMonitoringRunStatusEnum.failed,
]);

// How often to poll a run while it is queued/running.
export const AI_MONITORING_RUN_POLL_INTERVAL_MS = 4000;

export function isAiMonitoringRunTerminal(status: AiMonitoringRunStatusEnum): boolean {
    return AI_MONITORING_TERMINAL_RUN_STATUSES.has(status);
}
